const KNOWN_ACRONYMS: Record<string, string> = {
  fca: 'FCA',
  fos: 'FOS',
  tcf: 'TCF',
};

const TCF_LABEL_NAMES_BY_ID: Record<string, string> = {
  tcf_outcome_1_fair_treatment: 'Outcome 1 - Fair Treatment',
  tcf_outcome_2_suitable_products: 'Outcome 2 - Suitable Products And Services',
  tcf_outcome_3_clear_information: 'Outcome 3 - Clear Information',
  tcf_outcome_4_suitable_advice: 'Outcome 4 - Suitable Advice',
  tcf_outcome_5_product_performance: 'Outcome 5 - Product Performance',
  tcf_outcome_6_no_post_sale_barriers: 'Outcome 6 - No Post-Sale Barriers',
};

const normalizeLabelId = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const formatWord = (word: string): string => {
  if (!word) return '';
  if (word === '-') return word;

  if (word.includes('-')) {
    return word.split('-').map(formatWord).join('-');
  }

  const acronym = KNOWN_ACRONYMS[word.toLowerCase()];
  if (acronym) return acronym;
  if (/^\d+$/.test(word)) return word;

  return word[0].toUpperCase() + word.slice(1).toLowerCase();
};

/**
 * Formats a taxonomy identifier or raw display value for UI.
 */
export const formatTaxonomyLabel = (raw: string): string => {
  if (!raw) return '';

  const trimmed = raw.trim();
  const normalized = /[_]/.test(trimmed) || !/\s/.test(trimmed)
    ? trimmed.replace(/[-_]+/g, ' ')
    : trimmed;

  return normalized
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(formatWord)
    .join(' ');
};

const formatTcfOutcomeLabel = (raw: string, labelId?: string): string => {
  const canonical = TCF_LABEL_NAMES_BY_ID[normalizeLabelId(labelId || raw)];
  if (canonical) return canonical;

  const canonicalFromRaw = TCF_LABEL_NAMES_BY_ID[normalizeLabelId(raw)];
  if (canonicalFromRaw) return canonicalFromRaw;

  const withoutPrefix = formatTaxonomyLabel(raw).replace(/^TCF\s+/i, '').trim();
  const match = withoutPrefix.match(/^Outcome\s+([1-6])(?:\s*-\s*|\s+)(.+)$/i);
  if (!match) return withoutPrefix;

  return `Outcome ${match[1]} - ${match[2].trim()}`;
};

/**
 * Formats conduct labels for display without changing stored taxonomy IDs.
 */
export const formatConductLabel = (
  raw: string,
  variant?: string | null,
  labelId?: string,
): string => {
  const normalizedId = normalizeLabelId(labelId || raw);
  const looksLikeTcf = (
    variant === 'tcf'
    || normalizedId.startsWith('tcf_outcome_')
    || /^tcf\s+outcome\s+\d/i.test(raw.trim())
  );

  if (looksLikeTcf) {
    return formatTcfOutcomeLabel(raw, labelId);
  }

  return formatTaxonomyLabel(raw);
};
